import {useAccessToken, useIsUnitUser, useUserClaimsData} from "../../services/auth"
import {useParams} from "react-router-dom"
import React from "react"
import {
	CheckActionButtons,
	CheckDepositInfo,
	disabledActionsEnsentaTooltipValue,
	getButtonsToShow,
} from "../../components/CheckDeposits/CheckDepositInfo"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {
	CheckDeposit,
	CheckDepositStatus,
	CheckDepositVendors,
	CheckImageSide,
	getCheckDepositImage,
	getCheckDepositRequest,
} from "../../resources/checkDeposits"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import TitleBar, {MetaItemProps} from "../../components/TitleBar/TitleBar"
import {kebabCase, startCase} from "lodash"
import classNames from "classnames"
import {hasPermission} from "../../services/permission"
import MainSectionLayout from "../../containers/MainSection/MainSectionLayout"
import {OneProgramAdmonition} from "../../components/OneProgranAdmonition/OneProgramAdmonition"
import {Org} from "../../resources/org"
import {useQueryState} from "use-location-state"
import {AsyncResultRequestState} from "../../types/asyncResult"
import {ErrorDocument, OkDocument} from "../../resources/common"
import {Account} from "../../resources/account"
import {Customer} from "../../resources/customer"
import {CheckDepositAssignments, CheckDepositAssignmentsProps} from "./CheckDepositAssignments"
import {isProdEnv, isSandboxOrLocalEnv} from "../../utilities/environment"
import {AssigneeType, AssignmentStage} from "../../resources/assignments"
import {TabsMeta, TitleBarTabs} from "../../components/TitleBarTabs/TitleBarTabs"
import {IcomoonIconName} from "../../components/Icon/icons"
import {AssignmentsAmountIndicator} from "../../components/AssignmentAmountIndicator/AssignmentsAmountIndicator"
import {CheckImageCard} from "../../components/CheckDeposits/CheckImageCard"

export enum CheckDepositTabConstants {
	FullCheckDepositDetails = "FullCheckDepositDetails",
	ActionsRequired = "ActionsRequired",
}

export const checkDepositTabQueryStateName = "checkDepositTab" as const

interface CheckDepositMainSectionProps {
	frontImage: ArrayBuffer | undefined | null
	backImage: ArrayBuffer | undefined | null
	checkId: string
	cd: OkDocument<CheckDeposit>
	accessToken: string
	refresh: () => void
	getCheckDeposit: AsyncResultRequestState<OkDocument<CheckDeposit>, ErrorDocument>
}

function CheckDepositMainSection({
	frontImage,
	backImage,
	checkId,
	cd,
	accessToken,
	refresh,
	getCheckDeposit,
}: CheckDepositMainSectionProps) {
	return (
		<MainSectionLayout
			outerWrapperClassname={"check-deposit-full-with-details"}
			fullWidth={true}
			renderAfter={
				<div className="check-image-card-section">
					<CheckImageCard
						frontImage={frontImage}
						backImage={backImage}
						id={checkId}
						status={cd.data.attributes.status}
					/>
				</div>
			}
		>
			<CheckDepositInfo
				id={checkId}
				token={accessToken}
				refresh={refresh}
				useDrawer={false}
				checkDepositAsyncResult={getCheckDeposit}
			/>
		</MainSectionLayout>
	)
}

interface CheckDepositPageContentProps {
	isShowAssignments: boolean
	currentTab: CheckDepositTabConstants
	checkDepositAsMainSectionProps: CheckDepositMainSectionProps
	checkDepositAssignmentsProps: CheckDepositAssignmentsProps
}

function CheckDepositTabs({
	currentTab,
	setCurrentTab,
	checkDeposit,
	refreshToken,
}: {
	currentTab: CheckDepositTabConstants
	setCurrentTab: (tab: CheckDepositTabConstants) => void
	checkDeposit: CheckDeposit
	refreshToken: number
}) {
	const accessToken = useAccessToken()
	const claims = useUserClaimsData()
	const assigneeType = startCase(claims.userType) as AssigneeType
	const liClassName = "payment-tab-li" as const
	const commonTabProps = {liClassName}
	const tabs: TabsMeta<CheckDepositTabConstants>[] = [
		{
			label: startCase(CheckDepositTabConstants.FullCheckDepositDetails),
			key: CheckDepositTabConstants.FullCheckDepositDetails,
			prefixIcon: "payment-box-incoming" as IcomoonIconName,
		},
		{
			label: startCase(CheckDepositTabConstants.ActionsRequired),
			key: CheckDepositTabConstants.ActionsRequired,
			prefixIcon: "dialpad--business-products" as IcomoonIconName,
			suffixElement: (
				<AssignmentsAmountIndicator
					findAssignmentsArgs={{
						approvals: checkDeposit.relationships.approvals?.data.id,
						accessToken,
						assignees: [assigneeType],
						stages: [AssignmentStage.Pending],
						limit: 0,
					}}
					refreshToken={refreshToken}
					isActive={currentTab == CheckDepositTabConstants.ActionsRequired}
				/>
			),
		},
	].map((tab) => ({...tab, ...commonTabProps}))
	return <TitleBarTabs currentTab={currentTab} setCurrentTab={setCurrentTab} tabs={tabs} />
}

function CheckDepositPageContent({
	isShowAssignments,
	currentTab,
	checkDepositAsMainSectionProps,
	checkDepositAssignmentsProps,
}: CheckDepositPageContentProps) {
	if (!isShowAssignments) {
		return <CheckDepositMainSection {...checkDepositAsMainSectionProps} />
	}
	switch (currentTab) {
		case CheckDepositTabConstants.FullCheckDepositDetails:
			return <CheckDepositMainSection {...checkDepositAsMainSectionProps} />
		case CheckDepositTabConstants.ActionsRequired:
			return <CheckDepositAssignments {...checkDepositAssignmentsProps} />
	}
}

function hasApprovals(checkDeposit: CheckDeposit): boolean {
	return !!checkDeposit.relationships.approvals
}

function isShowAssignments(isUnitUser: boolean, checkDeposit: CheckDeposit, assignmentsView: boolean) {
	const sandboxAndLocalOrgIds = ["3475", "2654", "5297", "5314", "5326", "5328", "5329", "5335", "5336"]
	const sandboxAndLocalBankIds = ["20", "26"]
	const showAssignmentsOnSandBox =
		isSandboxOrLocalEnv() &&
		(sandboxAndLocalOrgIds.includes(checkDeposit.relationships.org?.data.id ?? "") ||
			sandboxAndLocalBankIds.includes(checkDeposit.relationships.bank?.data.id ?? "") ||
			isUnitUser)
	const showAssignmentsOnProd = isProdEnv()
	return hasApprovals(checkDeposit) && assignmentsView && (showAssignmentsOnSandBox || showAssignmentsOnProd)
}

export default function CheckDepositPage({assignmentsView = false}: {assignmentsView?: boolean}) {
	const accessToken = useAccessToken()
	const isUnitUser = useIsUnitUser()
	const {checkId = ""} = useParams<{checkId: string}>()
	const [refreshToken, refresh] = useRefreshToken()

	const [currentTab, setCurrentTab] = useQueryState<CheckDepositTabConstants>(
		checkDepositTabQueryStateName,
		CheckDepositTabConstants.ActionsRequired
	)

	const getCheckDeposit = useAsyncResult(() => getCheckDepositRequest({accessToken, id: checkId}), [refreshToken])

	const frontImageState = useAsyncResult(() =>
		getCheckDepositImage({
			accessToken,
			id: checkId,
			side: CheckImageSide.Front,
		})
	)
	const backImageState = useAsyncResult(() =>
		getCheckDepositImage({
			accessToken,
			id: checkId,
			side: CheckImageSide.Back,
		})
	)
	const frontImage = frontImageState.match(
		() => undefined,
		(image) => image,
		() => null
	)
	const backImage = backImageState.match(
		() => undefined,
		(image) => image,
		() => null
	)

	return (
		<>
			{getCheckDeposit.match(
				() => null,
				(cd) => {
					const org = cd.included && (cd.included.find((i) => (i.type as string) === "org") as unknown as Org)
					return (
						<>
							<TitleBar
								title={`#${checkId}`}
								breadcrumbs={[{text: "Check Deposits", path: "/check-deposits/"}]}
								status={{
									text: startCase(cd.data.attributes.status),
									className: classNames("check-deposit-status", kebabCase(cd.data.attributes.status)),
								}}
								meta={[
									{
										text: "Account",
										icon: "bank-account--business-products",
										path: `/accounts/${cd.data.relationships.account?.data.id}`,
									},
									{
										text: "Customer",
										icon: "user-geometric-action-money-fund---users",
										path: `/customer/${cd.data.relationships.customer?.data.id}`,
									},
									...(cd.data.relationships.account?.data.id && cd.data.relationships.transaction?.data.id
										? [
												{
													text: "Transaction",
													icon: "money-transfer--business-products",
													path: `/transaction/${cd.data.relationships.account?.data.id}/${cd.data.relationships.transaction?.data.id}`,
												} as MetaItemProps,
										  ]
										: []),
								]}
							>
								{!hasApprovals(cd.data) &&
									!isShowAssignments(isUnitUser, cd.data, assignmentsView) &&
									!assignmentsView &&
									hasPermission("checkDeposit.approve", "update") && (
										<CheckActionButtons
											id={checkId}
											refresh={refresh}
											buttons={getButtonsToShow(cd.data.attributes.status, cd.data.attributes.vendor)}
											title={
												cd.data.attributes.vendor === CheckDepositVendors.Ensenta
													? disabledActionsEnsentaTooltipValue
													: ""
											}
											disabled={cd.data.attributes.vendor === CheckDepositVendors.Ensenta}
											vendor={cd.data.attributes.vendor}
										/>
									)}
								{hasApprovals(cd.data) && isShowAssignments(isUnitUser, cd.data, assignmentsView) && (
									<div style={{display: "flex", flexDirection: "column"}}>
										<CheckDepositTabs
											currentTab={currentTab}
											setCurrentTab={setCurrentTab}
											checkDeposit={cd.data}
											refreshToken={refreshToken}
										/>
										{cd.data.attributes.vendor === CheckDepositVendors.Veryfi &&
											cd.data.attributes.status == CheckDepositStatus.Pending &&
											hasPermission("checkDeposit.approve", "update") && (
												<div style={{marginTop: "8px", marginRight: "30px", marginLeft: "auto"}}>
													<CheckActionButtons
														id={checkId}
														refresh={refresh}
														buttons={["Reject"]}
														title={""}
														disabled={false}
														vendor={cd.data.attributes.vendor}
													/>
												</div>
											)}
									</div>
								)}
							</TitleBar>
							{org?.relationships?.parentOrg && <OneProgramAdmonition />}
							<CheckDepositPageContent
								isShowAssignments={isShowAssignments(isUnitUser, cd.data, assignmentsView)}
								currentTab={currentTab}
								checkDepositAsMainSectionProps={
									{
										frontImage,
										backImage,
										checkId,
										cd,
										accessToken,
										refresh,
										getCheckDeposit,
									} as CheckDepositMainSectionProps
								}
								checkDepositAssignmentsProps={
									{
										checkDeposit: cd.data,
										included: cd.included as Array<Account | Customer>,
										frontImage: frontImage,
										backImage: backImage,
										refreshToken,
										refresh,
									} as CheckDepositAssignmentsProps
								}
							/>
						</>
					)
				},
				(_) => null
			)}
		</>
	)
}
